import React, { useMemo } from 'react';
import { useRefinementList } from 'react-instantsearch';

/**
 * Wrapper for the legacy connectRefinementList HOC from react-instantsearch-dom v6.
 * This provides the same API but uses the v7 react-instantsearch hook internally.
 *
 * CRITICAL: hookParams must be memoized to prevent infinite re-render loops.
 * Only the valid hook parameters are extracted and their values used as dependencies.
 *
 * @param {Function} Component - A component that receives refinement list data as props
 * @returns {React.Component} - A wrapped component connected to InstantSearch refinement list
 */
export const connectRefinementList = Component => {
  const ConnectedComponent = props => {
    const {
      attribute,
      operator,
      limit,
      showMore,
      showMoreLimit,
      sortBy,
      escapeFacetValues,
      transformItems,
    } = props;

    // Memoize hookParams to prevent infinite re-renders - only changes when actual params change
    const hookParams = useMemo(
      () => ({
        attribute,
        operator,
        limit,
        showMore,
        showMoreLimit,
        sortBy,
        escapeFacetValues,
        transformItems,
      }),
      [
        attribute,
        operator,
        limit,
        showMore,
        showMoreLimit,
        sortBy,
        escapeFacetValues,
        transformItems,
      ]
    );

    const refinementListData = useRefinementList(hookParams);

    return <Component {...props} {...refinementListData} />;
  };

  ConnectedComponent.displayName = `connectRefinementList(${Component.displayName ||
    Component.name ||
    'Component'})`;

  return ConnectedComponent;
};

export default connectRefinementList;
